import React, { useEffect, useRef, useState, useMemo } from 'react'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import scrollgrid from '@fullcalendar/scrollgrid'
import { v4 as uuid } from 'uuid'
import { isBefore, addMinutes, format, parse, isSameDay } from 'date-fns'
import './style.css'
import { useCSSEffect } from 'Data/useCSSEffect.js'
import SlotDialog from './SlotDialog/index.js'
import { UNTEMPLATED_SCHEDULING_ALLOWED_TYPE_OVERLAPS } from 'Data/constants.js'
import {
  currentTzDate,
  formatTzDate,
  minutesToTimestamp,
  timeToMinutes,
  twoDigitsTime,
  parseLocalDatetimeStringToZonedDate,
  doesEventOverlapAnyOtherEvent,
} from 'Data/format.js'
import pointerMovePlugin from './pointerMovePlugin.js'
import useOnPrint from './useOnPrint.js'
import useCustomComponentRender from './useCustomComponentRender.js'
import useEventHandle from './useEventHandle.js'
import useSlotHandle from './useSlotHandle.js'

/**
 * @typedef {import('@fullcalendar/react').default['props']} FullCalendarProps
 * @typedef {InstanceType<import('@fullcalendar/core')['Calendar']>} FullCalendarApi
 * @typedef {Object} SlotRef
 * @property {object | null} schedule_note
 */

let schedulerLicenseKey = process.env.REACT_APP_FULL_CALENDAR_API_KEY
let SLOT_MIN_WIDTH_PX = 167 // Default column width
let FOCUS_EVENT_DELAY_MS = 300 // Delay when focusing event, as it causes weird janking (and sometimes its not yet available in its internal db)
let RERENDER_DELAY_MS = 50 // For scheduling (when adding/removing custom events)
let SNAP_DURATION = '00:05:00'

export default function FullCalendarTimegrid({
  events,
  resources,
  date,
  location,
  viewPath,
  slotMinTime,
  slotMaxTime,
  slotInterval = 10,
  slotLabelMinutes = 10,
  hasAppointmentTemplateFilters,
  resourceWidth,
  focusedEventId,
  schedulingConfig,
  hipaa,
}) {
  let [showSlotDialog, setShowSlotDialog] = useState(false)
  let slotDialogElementId = 'slot-dialog'
  /** @type {React.RefObject<import('@fullcalendar/react').default | null>} */
  let calendarRef = useRef(null)
  let timeZoneId = useMemo(() => location.time_zone_id, [location])
  let expandRows = useMemo(() => slotInterval === 30, [slotInterval])

  let {
    renderEventContent,
    renderResourceContent,
    renderSlotLabelContent,
    renderNowIndicatorContent,
  } = useCustomComponentRender({ viewPath })

  let {
    onEventMouseEnter,
    onEventMouseLeave,
    onEventResize,
    onEventDrop,
    onEventOverlap,
    onEventDragStart,
    onEventDragStop,
    onEventAllow,
    onEventReceive,
  } = useEventHandle({ viewPath, calendarRef })

  /** @type {React.RefObject<SlotRef>} */
  let slotRef = useRef({
    element: null,
    schedule_note: null,
  })
  let { onSelectOverlap, onSelectSlot, onUnselectSlot } = useSlotHandle({
    viewPath,
    slotRef,
    setShowSlotDialog,
  })

  let printFilename = useMemo(
    () =>
      `${location.name} ${formatTzDate(
        date,
        'EEEE, MMMM dd, yyyy',
        timeZoneId
      )}.pdf`,
    [location.name, date, timeZoneId]
  )

  useOnPrint({
    calendarRef,
    hipaa,
    fileName: printFilename,
    viewPath,
  })

  let currentDate = useMemo(() => {
    return formatTzDate(
      currentTzDate(timeZoneId),
      "yyyy-MM-dd'T'HH:mm:ssXXX",
      timeZoneId
    )
  }, [timeZoneId])

  let slotDuration = useMemo(
    () => `00:${twoDigitsTime(slotInterval)}:00`,
    [slotInterval]
  )

  /**
   * @description Scrolls (focuses) to specific event
   *  - only if event id is provided and exists
   *  - only once (when id changes)
   *
   * TODO: this only solves vertical scroll/focus
   *  - move appointment overlay to other side if overlapping
   *  - figure out horizontal scroll (to resource)
   */
  useEffect(() => {
    if (!focusedEventId) return
    async function run() {
      await new Promise(resolve => {
        setTimeout(resolve, FOCUS_EVENT_DELAY_MS)
      })
      /** @type {FullCalendarApi | undefined} */
      let api = calendarRef.current?.getApi?.()
      if (!api) return
      let event = api.getEventById(focusedEventId)
      if (!event || !event.start) return
      let minute = event.start.getUTCHours() * 60 + event.start.getUTCMinutes()
      api.scrollToTime({ minute })
    }
    run()
  }, [focusedEventId])

  /**
   * @description CSS effect to highlight the primary and secondary lane
   *  - primary lane is the one that starts at slotMinTime
   *  - secondary lane is the one that starts at slotMinTime + 30 minutes
   *  - only applies if slotInterval is less or equal 10 minutes
   */
  useCSSEffect(() => {
    let slotMinDate = parse(slotMinTime, 'H:mm:ss', new Date())
    let primaryLaneSuffix = format(slotMinDate, ':mm:ss')

    let css = `
      .fc .fc-timegrid-slot-lane[data-time$='${primaryLaneSuffix}'] {
        border-top-color: var(--fc-border-primary-lane-color);
      }

      .fc .fc-timegrid-slot[data-time$='${primaryLaneSuffix}'] .fc-timegrid-slot-label-cushion:before {
        display: block;
      }
    `

    if (slotInterval <= 10) {
      let secondaryLaneSuffix = format(addMinutes(slotMinDate, 30), ':mm:ss')
      css += `
        .fc .fc-timegrid-slot-lane[data-time$='${secondaryLaneSuffix}'] {
          border-top-color: var(--fc-border-primary-lane-color);
        }
      `
    }

    return css
  }, [slotInterval, slotMinTime])

  useCSSEffect(() => {
    if (hasAppointmentTemplateFilters) {
      return `.fc-timegrid-event { width: 92%; }`
    }
  }, [hasAppointmentTemplateFilters])

  /**
   * Handle untemplated slots while scheduling
   */
  let candidateUntemplatedHoverId = useRef(null)
  useEffect(() => {
    if (
      typeof schedulingConfig?.duration !== 'number' ||
      schedulingConfig?.conflict_permission !== true
    ) {
      return
    }

    let api = calendarRef.current?.getApi?.()

    /** @returns {void} */
    function pointerMoveCancelHandler() {
      if (candidateUntemplatedHoverId.current) {
        api?.getEventById(candidateUntemplatedHoverId.current)?.remove?.()
      }
    }

    /** @returns {void} */
    function pointerMoveHandler(
      /** @type {{ data: import('./pointerMovePluginTypes.d.ts').PointerMoveData }} */
      event
    ) {
      if (candidateUntemplatedHoverId.current) {
        api?.getEventById(candidateUntemplatedHoverId.current)?.remove?.()
      }

      let {
        data: { conflicts, resourceId, startTime, date: event_date },
      } = event

      if (
        conflicts.appointmentCandidate ||
        conflicts.appointmentCandidateUntemplated ||
        conflicts.event
      ) {
        return
      }

      let resource = api?.getResourceById?.(resourceId)
      if (!resource) return

      let start_min = timeToMinutes(startTime)
      let end_min = start_min + schedulingConfig.duration
      let start = minutesToTimestamp(date, timeZoneId, start_min)
      let end = minutesToTimestamp(date, timeZoneId, end_min)
      let events = api?.getResourceById(resourceId)?.getEvents() ?? []
      let notAllowedOverlapEvents = events
        .filter(
          item =>
            !UNTEMPLATED_SCHEDULING_ALLOWED_TYPE_OVERLAPS.includes(
              item.extendedProps.type
            ) &&
            (item.extendedProps.type !== 'candidate' || item.startStr <= start)
        )
        .map(item => ({ start: item.startStr, end: item.endStr }))

      if (
        !isBefore(
          currentTzDate(timeZoneId),
          parseLocalDatetimeStringToZonedDate(start, timeZoneId)
        ) ||
        doesEventOverlapAnyOtherEvent({ start, end }, notAllowedOverlapEvents)
      ) {
        return
      }

      let id = `${uuid()}-candidate-untemplated-hover`
      candidateUntemplatedHoverId.current = id

      api?.addEvent({
        id,
        type: 'candidate-untemplated-hover',
        classNames: ['candidate-untemplated-hover'],
        display: 'background',
        start,
        end,
        resourceId,
        // should be same as tab.scheduling.untemplated_slot (except id)
        config: {
          start_min,
          end_min,
          date: event_date,
          chair_id: resourceId,
          chair_name: resource.extendedProps.name,
        },
        extendedProps: {
          description: `${schedulingConfig.appointment_type_name} - ${resource.extendedProps.name}`,
        },
      })
    }

    api?.on(
      // @ts-expect-error custom event type from pointerMovePlugin
      'pointerMove',
      pointerMoveHandler
    )

    api?.on(
      // @ts-expect-error custom event type from pointerMovePlugin
      'pointerMoveCancel',
      pointerMoveCancelHandler
    )

    return () => {
      api?.off(
        // @ts-expect-error custom event type from pointerMovePlugin
        'pointerMoveCancel',
        pointerMoveHandler
      )
      api?.off(
        // @ts-expect-error custom event type from pointerMovePlugin
        'pointerMove',
        pointerMoveHandler
      )
    }
  }, [schedulingConfig, date, timeZoneId])

  /**
   * Update calendar date managed from outside
   */
  useEffect(() => {
    let api = calendarRef.current?.getApi?.()

    if (api) {
      let calendarDate = formatTzDate(
        date,
        "yyyy-MM-dd'T'HH:mm:ssXXX",
        timeZoneId
      )
      let currentDate = currentTzDate(timeZoneId)
      let currentTime = formatTzDate(currentDate, 'HH:mm', timeZoneId)

      // https://github.com/fullcalendar/fullcalendar/issues/7448#issuecomment-1751518347
      queueMicrotask(() => {
        api.gotoDate(calendarDate)
        // Setting default time to 05:00 (AM) since this block might be executed twice if we make it
        // dependent on slotMinTime (days might start at different times, so date is set first
        // and then slotMinTime changes based on resource query, making this effect to trigger twice)
        api.scrollToTime(isSameDay(currentDate, date) ? currentTime : '05:00')
      })
    }
  }, [date, timeZoneId])

  /**
   * Handle right click on empty slot to show dialog instead of browser context menu
   */
  useEffect(() => {
    function handleRightClick(event) {
      event.preventDefault()
      if (event.target.classList.contains('fc-highlight')) {
        setShowSlotDialog(true)
      }
    }

    let calendarElement = calendarRef.current?.getApi()?.el
    calendarElement.addEventListener('contextmenu', handleRightClick)
    return () => {
      calendarElement.removeEventListener('contextmenu', handleRightClick)
    }
  }, [])

  useEffect(() => {
    let isBodyScaled = Boolean(
      document.body.classList.contains('mobile-scaled')
    )
    let wrapper = document.querySelector('.calendar-wrapper')
    let container = document.querySelector('.calendar-container')

    if (!container || !wrapper) return

    if (isBodyScaled) {
      container.classList.add('mobile-unscaled')
      wrapper.classList.add('mobile-scaled')
    } else {
      container.classList.remove('mobile-unscaled')
      wrapper.classList.remove('mobile-scaled')
    }
  }, [])

  return (
    <div className="calendar-wrapper">
      <div className="calendar-container">
        <FullCalendar
          schedulerLicenseKey={schedulerLicenseKey}
          ref={calendarRef}
          timeZone={timeZoneId}
          plugins={[
            pointerMovePlugin,
            timeGridPlugin,
            interactionPlugin,
            resourceTimeGridPlugin,
            scrollgrid,
          ]}
          initialView="resourceTimeGridDay"
          droppable={true} // Enables dropping events into the calendar
          editable={true} // Enables event edition - for example, drag and drop - for the calendar
          headerToolbar={false} // Hides toolbar with today, next, previous actions
          events={events}
          eventMouseEnter={onEventMouseEnter}
          eventMouseLeave={onEventMouseLeave}
          resourceOrder={'position'}
          resources={resources}
          now={currentDate} // Current date
          nowIndicatorContent={renderNowIndicatorContent}
          rerenderDelay={RERENDER_DELAY_MS}
          eventContent={renderEventContent} // Custom event component
          resourceLabelContent={renderResourceContent} // Custom resource component
          allDaySlot={false} // Display specific slot/row below calendar header for all-day events
          slotMinTime={slotMinTime} // Earliest time to display on the time axis
          slotMaxTime={slotMaxTime} // Latest time to display on the time axis
          slotDuration={slotDuration} // Slot duration in minutes
          snapDuration={SNAP_DURATION} // Slot granularity for slot selection or where an event can be dropped
          slotLabelInterval={{ minutes: slotLabelMinutes }} // Minutes in which the slot label is displayed
          slotLabelContent={renderSlotLabelContent} // Custom label to render for every slot
          nowIndicator={true} // Line that indicates the current time
          scrollTimeReset={false} // Avoid resetting scrollTime every time the date changes
          slotMinWidth={SLOT_MIN_WIDTH_PX}
          dayMinWidth={resourceWidth}
          height={'100%'}
          eventResourceEditable={false}
          selectable={true}
          selectOverlap={onSelectOverlap}
          eventOverlap={onEventOverlap}
          select={onSelectSlot}
          unselect={onUnselectSlot}
          unselectCancel={`#${slotDialogElementId}`}
          eventResize={onEventResize}
          eventDrop={onEventDrop}
          expandRows={expandRows}
          eventDragStart={onEventDragStart}
          eventDragStop={onEventDragStop}
          eventAllow={onEventAllow} // Restrict dragging/dropping actions for some cases
          eventReceive={onEventReceive}
          eventOrder={'priority,start,-duration,allDay,title'} // priority + the defaults according to docs https://fullcalendar.io/docs/eventOrder
        />
        <SlotDialog
          viewPath={`${viewPath}/SlotDialog`}
          slotDialogElementId={slotDialogElementId}
          showDialog={showSlotDialog}
          schedule_note={slotRef.current.schedule_note}
          onClose={() => {
            let api = calendarRef.current?.getApi?.()
            api?.unselect()

            setShowSlotDialog(false)
          }}
        />
      </div>
    </div>
  )
}
